import {
    CreateEventCommand,
    EventTypeEnum,
    EventVm,
    EventsClient,
    UpdateEventCommand,
} from '../utils/api';
import { OutboxOp } from './offlineEventStore';

/**
 * FluentValidation requires unrelated nested payloads absent
 * ("'Card' must be empty."). Never send CreateEventCommand class instances
 * to JSON.stringify - fromJS/toJSON fill empty Card/Timeout/… objects.
 * Always flush a plain object with only the nested field for this typeId.
 */
export const serializeEventPayload = (raw: unknown): Record<string, unknown> => {
    const src = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const typeId = src.typeId as EventTypeEnum;
    const body: Record<string, unknown> = {
        matchId: src.matchId,
        typeId: src.typeId,
        periodId: src.periodId,
        minute: src.minute,
        seconds: src.seconds,
        isShootout: src.isShootout,
        activeHomeGoalkeeperMatchPlayerId: src.activeHomeGoalkeeperMatchPlayerId,
        activeAwayGoalkeeperMatchPlayerId: src.activeAwayGoalkeeperMatchPlayerId,
        refereeMistake: src.refereeMistake,
    };
    if (src.id != null) body.id = src.id;
    // Livescore dedupes on this key. A queued row may still omit it; flush stamps it.
    const clientEventKey = src.clientEventKey;
    if (typeof clientEventKey === 'string' && clientEventKey.trim()) {
        body.clientEventKey = clientEventKey.trim();
    }

    switch (typeId) {
        case EventTypeEnum.Shot:
            if (src.shot) body.shot = src.shot;
            break;
        case EventTypeEnum.Exclusion:
            if (src.exclusion) body.exclusion = src.exclusion;
            break;
        case EventTypeEnum.Timeout:
            if (src.timeout) body.timeout = src.timeout;
            break;
        case EventTypeEnum.Card:
            if (src.card) body.card = src.card;
            break;
        case EventTypeEnum.Swim_Off:
            if (src.swimoff) body.swimoff = src.swimoff;
            break;
        case EventTypeEnum.Challenge:
            if (src.challenge) body.challenge = src.challenge;
            break;
        case EventTypeEnum.Turnover:
            if (src.turnover) body.turnover = src.turnover;
            break;
        case EventTypeEnum.Goalkeeper_Change:
            if (src.goalkeeperChange) body.goalkeeperChange = src.goalkeeperChange;
            break;
        default:
            break;
    }
    return body;
};

/** Accept CreateEventCommand or plain object; always store/send a clean payload. */
export const payloadFromCommand = (
    command: CreateEventCommand | object
): Record<string, unknown> => {
    const raw =
        command &&
        typeof command === 'object' &&
        typeof (command as { toJSON?: () => unknown }).toJSON === 'function'
            ? (command as { toJSON: () => unknown }).toJSON()
            : command;
    return serializeEventPayload(raw);
};

/**
 * Rows already in IndexedDB were stored without clientEventKey.
 * Copy the outbox key onto the body so the POST matches the unique index.
 */
export const payloadWithClientEventKey = (
    payload: unknown,
    clientEventKey: string | undefined
): Record<string, unknown> => {
    const body = serializeEventPayload(payload);
    if (clientEventKey && clientEventKey.trim()) {
        body.clientEventKey = clientEventKey.trim();
    }
    return body;
};

/**
 * Single HTTP entry point for outbox flush (and any future direct send).
 * Same create/update/delete the screens used before offline support.
 */
export const flushEventMutation = async (
    op: OutboxOp,
    payload: unknown,
    serverEventId?: number,
    signal?: AbortSignal
): Promise<EventVm> => {
    const client = new EventsClient(undefined, {
        fetch: (url: RequestInfo, init?: RequestInit): Promise<Response> =>
            fetch(url, { ...init, signal: signal ?? init?.signal }),
    });
    if (op === 'create') {
        return client.create(serializeEventPayload(payload) as unknown as CreateEventCommand);
    }
    if (op === 'update') {
        const body = serializeEventPayload(payload);
        const id = serverEventId ?? (body.id as number | undefined);
        if (!id) throw new Error('Missing event id for update');
        return client.update(id, { ...body, id } as unknown as UpdateEventCommand);
    }
    const eventId =
        serverEventId ??
        (payload as { eventId?: number })?.eventId ??
        (payload as { id?: number })?.id;
    if (!eventId) throw new Error('Missing event id for delete');
    return client.delete(eventId);
};
